import { isMuted, setMuted as persistMute, getVolume, setVolume as persistVolume } from './storage';
import { setMenuMusicMuted, setMenuMusicVolume } from './menuMusic';

let audioCtx: AudioContext | null = null;

export function getCtx(): AudioContext {
  if (!audioCtx) {
    audioCtx = new AudioContext();
  }
  return audioCtx;
}

let muted = isMuted();
let volume = getVolume(); // 0–100

/** Returns a 0–1 gain multiplier based on the current volume setting */
export function getVolumeScale(): number {
  return volume / 100;
}

export function getMuted(): boolean {
  return muted;
}

export function getVolumeLevel(): number {
  return volume;
}

export function setVolumeLevel(v: number): void {
  volume = Math.max(0, Math.min(100, Math.round(v)));
  persistVolume(volume);
  setMenuMusicVolume(volume / 100);
}

export function toggleMute(): boolean {
  muted = !muted;
  persistMute(muted);
  setMenuMusicMuted(muted);
  return muted;
}

export function playKeystroke(): void {
  if (muted) return;
  const s = getVolumeScale();
  const ctx = getCtx();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);

  const freqs = [800, 900, 1000];
  osc.frequency.value = freqs[Math.floor(Math.random() * freqs.length)];
  osc.type = 'square';

  gain.gain.setValueAtTime(0.03 * s, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);

  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + 0.05);
}

export function playError(): void {
  if (muted) return;
  const s = getVolumeScale();
  const ctx = getCtx();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);

  osc.frequency.value = 200;
  osc.type = 'sawtooth';

  gain.gain.setValueAtTime(0.06 * s, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);

  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + 0.12);
}

export function playFanfare(): void {
  if (muted) return;
  const s = getVolumeScale();
  const ctx = getCtx();
  const notes = [523.25, 659.25, 783.99];

  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.frequency.value = freq;
    osc.type = 'sine';

    const startTime = ctx.currentTime + i * 0.08;
    gain.gain.setValueAtTime(0, startTime);
    gain.gain.linearRampToValueAtTime(0.08 * s, startTime + 0.1);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.8);

    osc.start(startTime);
    osc.stop(startTime + 0.8);
  });
}

export function playKeystrokeAtPitch(pitchMultiplier: number): void {
  if (muted) return;
  const s = getVolumeScale();
  const ctx = getCtx();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);

  const freqs = [800, 900, 1000];
  osc.frequency.value = freqs[Math.floor(Math.random() * freqs.length)] * pitchMultiplier;
  osc.type = 'square';

  gain.gain.setValueAtTime(0.03 * s, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);

  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + 0.05);
}
